import {InfiniteData} from '@tanstack/react-query';
import {produce} from 'immer';
import {useMemo} from 'react';
import {FeatureName, Project, ProjectStatus} from '@contentlab/types/project';
import {ProjectStatusUpdate} from '@contentlab/types/projectStatusUpdate';
import queryClient from '@/utils/reactQueryClient';
import QueryContext from '@/api/QueryContext';
import projectFetchers from '@/api/fetchers/content-studio/project';
import useProjectsStatusQuery from '@/hooks/content-studio/use-projects-status-query';
import useInfiniteReactQuery from '@/hooks/use-infinite-react-query';
import {CONTENT_LAB, PROJECTS} from '../consts';

type SortBy = 'name' | 'updated_at' | 'created_at';
type SortOrder = 'asc' | 'desc';

export interface UseProjectsQueryParams {
  updatedHoursAgo?: string | number;
  sortBy?: SortBy;
  sortOrder?: SortOrder;
  featureFilter?: FeatureName;
  statusFilter?: ProjectStatus;
  includeInProgress?: boolean;
  pageSize?: number;
  live?: boolean;
}

export interface ProjectsPageResponse {
  projects: Project[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const FILTERED_STATUSES = [ProjectStatus.PENDING_USER_ACTION];

function syncProjectsCache(
  params: Partial<UseProjectsQueryParams>,
  projectStatusUpdates: ProjectStatusUpdate[]
) {
  queryClient.setQueryData(
    [CONTENT_LAB, PROJECTS, params],
    (old: InfiniteData<ProjectsPageResponse> | undefined) => {
      if (!old) {
        return undefined;
      }

      // group project status updates by projectId for easier access
      const projectStatusMap = projectStatusUpdates.reduce(
        (acc: {[key: string]: ProjectStatusUpdate}, projectStatusUpdate) => {
          acc[projectStatusUpdate.projectId] = projectStatusUpdate;
          return acc;
        },
        {}
      );

      // sync project statuses in the cached infinite projects data
      return {
        ...old,
        pages: produce(old.pages, (draft: ProjectsPageResponse[]) => {
          draft.forEach((page) => {
            page.projects.forEach((project: Project) => {
              const statusUpdate = projectStatusMap[project.id];
              if (statusUpdate) {
                project.state = statusUpdate.progressInfo;
                project.status = statusUpdate.status as ProjectStatus;
              }
            });
          });
        }),
      };
    }
  );
}

function useProjectsQuery(params: UseProjectsQueryParams = {}) {
  const {live = true, ...restParams} = params;

  const response = useInfiniteReactQuery<ProjectsPageResponse>({
    queryKey: [CONTENT_LAB, PROJECTS, restParams],
    queryFn: ({pageParam = 1, ...rest}: QueryContext) =>
      projectFetchers.getProjects({pageParam, ...rest}),
    refetchOnWindowFocus: false,
    meta: {
      ...params,
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) {
        return undefined;
      }
      return lastPage.page < lastPage.totalPages
        ? lastPage.page + 1
        : undefined;
    },
  });

  const flatPages = useMemo(() => {
    const {pages = []} = response.data || {};
    return pages
      .flatMap((page) => page.projects || [])
      .filter((project) => !FILTERED_STATUSES.includes(project.status));
  }, [response.data]);

  useProjectsStatusQuery({
    enabled: live,
    updatedHoursAgo: 24,
    onChange: (reason, projectStatusUpdates: ProjectStatusUpdate[]) => {
      if (reason === 'UPDATE') {
        syncProjectsCache(restParams, projectStatusUpdates);
      } else {
        // project status was added or removed => invalidate the query to get the latest data
        queryClient.invalidateQueries({
          queryKey: [CONTENT_LAB, PROJECTS],
        });
      }
    },
  });

  return {
    ...response,
    data: flatPages,
  };
}

export default useProjectsQuery;
